/**
 * THE design-token source of truth for sportsmet.
 *
 * Everything the platform renders — web (`apps/app`, `apps/admin`, `apps/home`)
 * and native (`apps/mobile`) — is generated from this one object:
 *
 *   tokens.ts (here)  ──▶  tokensCss.ts        ──▶  tokens.css          (web, --sm-* custom properties)
 *                     ──▶  tailwindPreset.ts   ──▶  tailwind-preset.css (Tailwind v4 @theme mapping)
 *                     ──▶  native.ts           ──▶  @sportsmet/theme/native (React Native StyleSheet values)
 *
 * Constraints that keep the two platforms from drifting:
 *
 *  - This file imports nothing platform-specific. No DOM, no `react-native`,
 *    no CSS. It is plain data plus types, so the native entry point can import
 *    it directly rather than keeping a parallel copy.
 *  - Numbers are unitless (device-independent pixels). Units are added by the
 *    renderer that needs them, never stored here — React Native has no "px".
 *  - Colors are literal 6-digit hex. Dark values are CHOSEN, never an
 *    algorithmic darken of light: derived dark neutrals reliably produce
 *    invisible disabled states and washed-out placeholders.
 *  - `light` and `dark` carry identical key sets, asserted by `tokens.test.ts`,
 *    so a new semantic token cannot ship with only one mode implemented.
 *  - Every foreground/background pair listed in `CONTRAST_CONTRACT` is asserted
 *    at its WCAG 2.2 AA floor by `tokens.test.ts` against computed ratios. A
 *    token edit that breaks accessibility fails CI instead of shipping.
 */

/* ------------------------------------------------------------------------- *
 * Palette — raw color scales
 *
 * Scales are the paint. Nothing in `apps/**` or `packages/ui/**` reaches for a
 * scale step directly; screens consume the semantic aliases below, which is
 * what makes light and dark a single swap rather than a per-component `if`.
 * ------------------------------------------------------------------------- */

/** Sportsmet blue, the brand hue. Actions, links, active navigation. Anchored on #3067FF. */
const brand = {
  50: "#EFF3FF",
  100: "#E3EAFF",
  200: "#A3BDFF",
  300: "#7DA0FF",
  400: "#5583FF",
  500: "#3067FF",
  550: "#2A5CE6",
  575: "#2756D9",
  600: "#2450CC",
  700: "#1C3F9E",
  800: "#16306F",
  900: "#16224A",
  950: "#0C142B",
} as const;

/**
 * The secondary hue. It carries `accent` and the focus ring. The 550 half-step
 * clears 3:1 against both the dark elevated surface and the light page.
 */
const stadium = {
  50: "#E4F1F7",
  100: "#B8E0F5",
  200: "#7FC7EE",
  300: "#5FB8E8",
  400: "#2E9BD4",
  500: "#1183B8",
  550: "#0E78A6",
  600: "#0B6E99",
  700: "#095A7D",
  800: "#074861",
  900: "#13272F",
  950: "#061D28",
} as const;

/**
 * Locking soon, pending, needs attention. This is distinct from brand gold so
 * "pay attention" and "this is the brand" never look alike on a scoreboard.
 */
const orange = {
  50: "#FFF1E8",
  100: "#FFDCC4",
  200: "#FFBE92",
  300: "#FF9A57",
  400: "#F97316",
  500: "#DC5F0A",
  600: "#B84A05",
  700: "#8F3A03",
  800: "#6B2B02",
  900: "#3D1801",
  950: "#260F00",
} as const;

/** Won, alive, confirmed, on track. */
const green = {
  50: "#E9F8F0",
  100: "#C6EDD9",
  200: "#8FDCB6",
  300: "#43C883",
  400: "#22A868",
  500: "#1F8B57",
  600: "#1F7A4D",
  700: "#17603C",
  800: "#114A2E",
  900: "#0A2E1D",
  950: "#061E13",
} as const;

/** Eliminated, lost, destructive. */
const red = {
  50: "#FDEDEB",
  100: "#F9D3CF",
  200: "#F1A9A1",
  300: "#E8695C",
  400: "#D94A3B",
  500: "#C13A2E",
  600: "#A62F25",
  700: "#86261E",
  800: "#661D17",
  900: "#3B110D",
  950: "#260B08",
} as const;

/** Neutral informational — a shade cooler and flatter than the brand blue. */
const blue = {
  50: "#EDF4FC",
  100: "#D3E4F7",
  200: "#A8C9EE",
  300: "#7CB4E8",
  400: "#4A87CC",
  500: "#2E71B7",
  600: "#2563A6",
  700: "#1D4E83",
  800: "#163C64",
  900: "#102639",
  950: "#0A1926",
} as const;

/** Pool-type identity only — Pick'em. Not a status hue. */
const pink = {
  50: "#FEEEF6",
  100: "#FAD1E4",
  200: "#F1A7CA",
  300: "#E77EAF",
  400: "#D54D8D",
  500: "#BF2B70",
  600: "#A1215D",
  700: "#811849",
  800: "#641238",
  900: "#380A20",
  950: "#250715",
} as const;

/** Pool-type identity only — Fantasy. Deliberately not marketingColors.brand (#8E43F0), which is walled off for the sportsmet.com homepage. */
const purple = {
  50: "#F7EFFD",
  100: "#E7D3F8",
  200: "#CEABED",
  300: "#B685E0",
  400: "#8E4BC7",
  500: "#7C36BA",
  600: "#672A9D",
  700: "#52207E",
  800: "#401763",
  900: "#250E3A",
  950: "#180925",
} as const;

/**
 * The neutral ramp, warmed very slightly toward green so a surface next to the
 * stadium blue reads as paper rather than as a second, dirtier blue.
 *
 * Strictly monotonic by relative luminance (`tokens.test.ts` asserts it), which
 * is why a few half-steps exist: a semantic role needed a value that landed
 * between two hundreds, and inventing a nearby-but-different hex to keep the
 * naming tidy is exactly the drift this ramp is here to prevent.
 */
const neutral = {
  0: "#FFFFFF",
  50: "#F6F6F4",
  100: "#EEF2EE",
  200: "#E6E5DE",
  300: "#C7C6BC",
  400: "#ADB6AC",
  500: "#939A92",
  550: "#7E877E",
  575: "#767E75",
  600: "#686F65",
  700: "#565D58",
  800: "#4A524B",
  850: "#39423B",
  875: "#262D26",
  900: "#1E2622",
  925: "#171D19",
  950: "#151A17",
  975: "#0F1411",
  1000: "#000000",
} as const;

export const palette = {
  brand,
  stadium,
  orange,
  green,
  red,
  blue,
  pink,
  purple,
  neutral,
} as const;

export type typePalette = typeof palette;

/* ------------------------------------------------------------------------- *
 * Marketing surface — scheme-invariant on purpose
 * ------------------------------------------------------------------------- */

/**
 * The sportsmet.com homepage is a single fixed dark design, the same in light
 * and dark. That is identity, not theming — the same category as a brand hero
 * image. These live outside `typeSemanticColors` so they are emitted from the
 * mode-INDEPENDENT half of `renderTokensCss` and can never be overridden
 * inside `@media (prefers-color-scheme: light)`, which after ISSUE-463 is the
 * only mode block the sheet has. `tokens.test.ts` asserts that.
 *
 * The failure the light/dark rule exists to prevent is a screen that half-uses
 * scheme-reactive tokens and becomes unreadable when the scheme flips. A
 * surface that is deliberately identical in both is not broken in either. What
 * is banned is mixing: the homepage reads these and NEVER reads
 * `--sm-color-surface` or `--sm-color-text`.
 */
export const marketingColors = {
  /** Every dark section background, and every section overlay. */
  ink: "#150E1F",
  /** Headings and body copy on ink. Also the footer light bar's background. */
  onInk: "#F3F1F6",
  /** Solid white: button and social-tile labels, and every contact input fill. */
  paper: "#FFFFFF",
  /** The solid brand SURFACE only — 3.78:1 as text on ink, which fails. */
  brand: "#8E43F0",
  /** Brand-colored TEXT on ink. 4.69:1. The footer legal links use this. */
  brandOnInk: "#9C5CF2",
  /** Brand-colored text and control edges on a light surface. 7.99:1 on paper. */
  brandOnLight: "#6300E2",
  /** Field-error copy and the invalid-input edge. `palette.red[300]`, not a new hue. */
  fieldError: red[300],
  facebook: "#3B5998",
  /** Black, not legacy Twitter blue: #1DA1F2 is 2.83:1 and fails even the 3.0 non-text floor. */
  x: "#000000",
  instagram: "#262626",
} as const;

export type typeMarketingColorToken = keyof typeof marketingColors;

/**
 * Section overlay opacities for the marketing homepage. Named rather than
 * written at the call site because contrast over a photograph cannot be proven
 * from a token pair, so the number itself is the contract — see ISSUE-060 D5,
 * which measured 0.65 as the point where the hero clears 4.5:1 against a pure
 * white worst-case pixel rather than only the 3.0 large-text floor.
 */
export const marketingOverlay = {
  heroOpacity: 0.65,
  contactOpacity: 0.8,
} as const;

/* ------------------------------------------------------------------------- *
 * Semantic color aliases
 * ------------------------------------------------------------------------- */

/**
 * The semantic contract. Both `light` and `dark` implement exactly this shape,
 * so "does dark mode have this token" is a compile error rather than a bug a
 * fan finds at night.
 *
 * Naming convention, applied to every status hue:
 *  - `<role>`      — the text/icon-safe value. Clears 4.5:1 on `bg` AND `surface`.
 *  - `<role>Solid` — the filled-badge/button background.
 *  - `on<Role>`    — text drawn ON `<role>Solid`. Clears 4.5:1 against it.
 *  - `<role>Tint`  — the subtle tinted surface. `<role>` clears 4.5:1 on it.
 */
export type typeSemanticColors = {
  /** Page background. */
  bg: string;
  /** Card / container background. */
  surface: string;
  /** Sheet, dialog, popover, toast background — one step off `surface`. */
  surfaceElevated: string;
  /** Recessed wells: a table header, a code block, an inset list. */
  surfaceSunken: string;
  /** The disc a team logo image sits on. Light in both modes so a dark logo stays readable at night (ISSUE-1087). */
  markLogoSurface: string;

  /** Primary text. */
  text: string;
  /** Secondary text — supporting facts, metadata. */
  textSecondary: string;
  /** Tertiary text — placeholders, timestamps. Still held to 4.5:1. */
  textTertiary: string;
  /** Text drawn on an inverted surface (a dark tooltip in light mode). */
  textInverse: string;

  /**
   * Brand actions, links, active nav, and thin brand indicators. Text-safe,
   * clears 4.5:1 on `bg` and `surface`; never use as a large light-mode fill.
   */
  primary: string;
  /** Hover state on brand text and links. */
  primaryHover: string;
  /** The brand blue fill for buttons and checked controls. Carries `onPrimary`. Identical in both modes. */
  primarySolid: string;
  /** Hover state on a `primarySolid` fill. */
  primarySolidHover: string;
  /** Pressed state on a `primarySolid` fill. */
  primarySolidActive: string;
  /** Text on a `primarySolid` fill. White in both modes, because the fill is one blue in both. */
  onPrimary: string;
  /** The one tinted surface a screen is allowed (never as the sole selected indicator). */
  primaryTint: string;

  /** Secondary stadium blue, text-safe. */
  accent: string;
  /** Secondary stadium blue as a fill. */
  accentSolid: string;
  /** Text on `accentSolid`. */
  onAccent: string;
  /** Tinted stadium-blue accent surface. */
  accentTint: string;

  /** Won, alive, confirmed — text-safe. */
  success: string;
  successSolid: string;
  onSuccess: string;
  successTint: string;

  /** Locking soon, pending, needs attention — text-safe. */
  warning: string;
  warningSolid: string;
  onWarning: string;
  warningTint: string;

  /** Eliminated, lost, destructive — text-safe. */
  error: string;
  /** Hover/active state on a destructive control. */
  errorHover: string;
  errorSolid: string;
  onError: string;
  errorTint: string;

  /** Neutral informational banner — text-safe. */
  info: string;
  infoSolid: string;
  onInfo: string;
  infoTint: string;

  /**
   * The two sides of a head to head comparison bar (ISSUE-1084): the away
   * team's segment and the home team's segment. A blue and an orange, so the
   * pair differs in hue and not just in lightness, and each clears 3:1
   * non-text contrast on `bg`, `surface`, and `surfaceElevated`. Theme
   * colors, never team colors.
   */
  comparisonAway: string;
  comparisonHome: string;

  /** Interactive control boundary (input, select, checkbox). Held to 3:1. */
  border: string;
  /** A boundary that has to carry weight — a selected row, a focused field. */
  borderStrong: string;
  /** Decorative divider. Not an interactive boundary, not held to 3:1. */
  borderHairline: string;
  /** The focus ring. Held to 3:1 against `bg`, `surface`, and `primary`. */
  focusRing: string;

  /** Scrim behind a dialog, sheet, or drawer. */
  scrim: string;

  /** Skeleton base and its animated sheen. */
  skeletonBase: string;
  skeletonSheen: string;

  /** Disabled control fill, its label, and its boundary. */
  disabledBg: string;
  disabledText: string;
  disabledBorder: string;
};

const light: typeSemanticColors = {
  bg: neutral[50],
  surface: neutral[0],
  surfaceElevated: neutral[0],
  surfaceSunken: neutral[50],
  markLogoSurface: neutral[0],

  text: neutral[950],
  textSecondary: neutral[700],
  textTertiary: neutral[600],
  textInverse: neutral[100],

  primary: brand[600],
  primaryHover: brand[700],
  primarySolid: brand[500],
  primarySolidHover: brand[600],
  primarySolidActive: brand[700],
  onPrimary: neutral[0],
  primaryTint: brand[100],

  accent: stadium[600],
  accentSolid: stadium[300],
  onAccent: neutral[950],
  accentTint: stadium[50],

  success: green[600],
  successSolid: green[600],
  onSuccess: neutral[0],
  successTint: green[50],

  warning: orange[700],
  warningSolid: orange[300],
  onWarning: neutral[950],
  warningTint: orange[50],

  error: red[500],
  errorHover: red[600],
  errorSolid: red[500],
  onError: neutral[0],
  errorTint: red[50],

  info: blue[600],
  infoSolid: blue[600],
  onInfo: neutral[0],
  infoTint: blue[50],

  comparisonAway: brand[500],
  comparisonHome: orange[500],

  border: neutral[550],
  borderStrong: neutral[700],
  borderHairline: neutral[200],
  focusRing: stadium[550],

  scrim: "rgba(15, 20, 17, 0.55)",

  skeletonBase: neutral[200],
  skeletonSheen: neutral[50],

  disabledBg: neutral[100],
  disabledText: neutral[600],
  disabledBorder: neutral[300],
};

const dark: typeSemanticColors = {
  bg: neutral[975],
  surface: neutral[925],
  surfaceElevated: neutral[900],
  surfaceSunken: neutral[1000],
  markLogoSurface: neutral[50],

  text: neutral[100],
  textSecondary: neutral[400],
  textTertiary: neutral[500],
  textInverse: neutral[950],

  primary: brand[300],
  primaryHover: brand[200],
  primarySolid: brand[500],
  primarySolidHover: brand[550],
  primarySolidActive: brand[575],
  onPrimary: neutral[0],
  primaryTint: brand[900],

  accent: stadium[300],
  accentSolid: stadium[300],
  onAccent: neutral[975],
  accentTint: stadium[900],

  success: green[300],
  successSolid: green[300],
  onSuccess: neutral[975],
  successTint: green[900],

  warning: orange[300],
  warningSolid: orange[300],
  onWarning: neutral[975],
  warningTint: orange[900],

  error: red[300],
  errorHover: red[200],
  errorSolid: red[300],
  onError: neutral[975],
  errorTint: red[900],

  info: blue[300],
  infoSolid: blue[300],
  onInfo: neutral[975],
  infoTint: blue[900],

  comparisonAway: brand[400],
  comparisonHome: orange[300],

  border: neutral[575],
  borderStrong: neutral[400],
  borderHairline: neutral[875],
  focusRing: stadium[550],

  scrim: "rgba(0, 0, 0, 0.68)",

  skeletonBase: neutral[875],
  skeletonSheen: neutral[850],

  disabledBg: neutral[900],
  disabledText: neutral[550],
  disabledBorder: neutral[850],
};

/* ------------------------------------------------------------------------- *
 * Space, radius, size
 * ------------------------------------------------------------------------- */

/**
 * The 4/8 ladder. These are the ONLY spacing values on
 * the platform; the numeric key is the Tailwind step, so `space.md` (16) is
 * `p-4`, and an off-ladder class like `py-2.5` has no token behind it.
 */
const space = {
  none: 0,
  xxs: 4,
  xs: 8,
  sm: 12,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 40,
} as const;

const radius = {
  none: 0,
  sm: 8,
  md: 12,
  lg: 16,
  full: 999,
} as const;

const size = {
  /** Default control height. Everything in a row shares it. */
  controlHeight: 48,
  /** Compact control height — still padded out to `touchTarget` via hit-slop. */
  controlHeightSm: 36,
  /** Hard floor for any interactive element on any touch surface. */
  touchTarget: 44,
  /** Android's own floor: Material puts it at 48dp, above the iOS/web 44. */
  touchTargetAndroid: 48,
  /** Icon sizes that pair with the two control heights. */
  iconSm: 16,
  iconMd: 20,
  iconLg: 24,
  /** `.sm-content-width` — the page column cap on wide screens. */
  contentWidth: 1120,
  /** A single reading column: forms, body copy, settings lists. */
  readingWidth: 672,
  /** A centered dialog's cap. A confirm never stretches across a tablet. */
  dialogWidth: 480,
  /** The fixed list pane in a two pane layout. The detail pane takes the rest. */
  listPaneWidth: 360,
  /**
   * The score row's status column at 640px and up (ISSUE-908), and the
   * compact one under 640 (Scores UX spec v1.0, section 1.7).
   *
   * PIXELS, not rem steps: the type scale is pixel based, so a status label is
   * the same width at every root font size, and a rem column would double
   * around it at the largest text scale. 152 holds the widest closed-set live
   * label ("Inn 24 · 444:44", about 102px in Inter 600 13.5px) on one line. 88
   * holds "Tue 11:00 PM" on one line or wraps it at its space to two; the label
   * is never ellipsized. The arithmetic is asserted in `tokens.test.ts`.
   */
  scoreStatusWidth: 152,
  scoreStatusCompactWidth: 88,
  scoreStatusPadding: 12,
  scoreLiveDot: 8,
  scoreLiveGap: 4,
  /**
   * The score row's own boxes, in pixels so they hold still at every text
   * scale (ISSUE-908 R5, Scores UX spec v1.0 section 1):
   *   rowMinHeight  8 + 28 + 4 + 28 + 8 = 76, two team lines and padding
   *                 (the spec table says 72; its own sum is 76)
   *   markSize      the round team mark on a team line
   *   inset         the row's horizontal padding, and the padding on each
   *                 side of the hairline before the status column
   *   gap           between the mark, the name cell and the score
   *   nameWidth     the floor the phone arithmetic is checked against; the
   *                 name cell itself picks a label that fits (`pickTeamLabel`)
   * The name cell arithmetic lives in `scores/scoreRowGeometry.ts` and is
   * asserted in `tokens.test.ts`. Do not restate it here.
   */
  scoreRowMinHeight: 76,
  scoreMarkSize: 28,
  /** The logo's inset inside the 28 row mark's disc (spec 1.2: 2px of inner
   *  padding), so a square logo clears the round edge and its hairline ring.
   *  A named size like `hairline`, not a spacing step. */
  scoreMarkLogoInset: 2,
  scoreRowInset: 12,
  scoreRowGap: 8,
  scoreNameWidth: 96,
  /** Filter chips and the strip's compact buttons: the compact control
   *  height, padded to `touchTarget` by its consumer. An alias, asserted equal
   *  to `controlHeightSm` in `tokens.test.ts`. */
  chipHeight: 36,
  /** The league view's day strip (Scores UX spec v1.0 section 4.2): a fixed
   *  64px row of fixed 48x56 day chips, or 72px minimum week chips. A chip
   *  never stretches to its row. */
  dayChipWidth: 48,
  dayChipHeight: 56,
  dayStripHeight: 64,
  weekChipMinWidth: 72,
  /** The game detail header's team mark. */
  gameHeaderMarkSize: 48,
  /** The reserve under the last scrollable element, the number behind
   *  `.sm-screen-bottom` and its mobile mirror. */
  screenBottomReserve: 64,
  /**
   * The page gutter around every screen, AppShell's `<main>`, in pixels so it
   * holds still at every text scale. A score row sits inside exactly one.
   */
  pageGutter: 16,
  /**
   * The league drill-down calendar column, 20rem at root 16, now pixels so it
   * does not double at root 32.
   */
  scoresCalendarTrack: 320,
  /** Compact ends here. Bottom sheet below, centered dialog at and above. */
  mobileBreakpoint: 768,
  /** Medium ends here. Expanded is this wide and up. */
  expandedBreakpoint: 1024,
  /** Hairline stroke. Not on the 4/8 ladder because a border is not spacing. */
  hairline: 1,
  /** Focus ring stroke and its offset from the control edge. */
  focusRingWidth: 2,
  focusRingOffset: 2,
  /* The marketing homepage's three measured containers and its two section
   * paddings, snapped from the live site's 825/900/1170 and 150/75 onto the
   * 8px rhythm. The spacing ladder permits named reserves on that rhythm; it
   * does not permit 825px written into a page. Max drift 4px, imperceptible
   * side by side. See ISSUE-060 R1. */
  marketingHeroWidth: 824,
  marketingPitchWidth: 896,
  marketingContactWidth: 1168,
  marketingSectionPadding: 152,
  marketingSectionPaddingCompact: 72,
  /**
   * Scores UX spec v1.0, P1 (sections 7 to 10): the game, team, player and
   * standings screens. Every number those sections name that no older token
   * already carries, so neither app writes one inline. Asserted in
   * `scoresDetailTokens.test.ts`.
   *
   *   gameHeaderCenterMinWidth  the face-off header's status column, between the two scores
   *   teamHeaderMarkSize        the team page header mark
   *   playerHeaderPhotoSize     the player page header headshot
   *   rosterHeadshotSize        one roster row's headshot, equal to `controlHeightSm`
   *   tabIndicatorHeight        the underline under the selected tab
   *   accentEdgeWidth           the primary left edge on a scoring drive or a followed standings row
   *   splitBarHeight            the team stats comparison bar
   *   livePillHeight            the live period pill in the game header
   *   resultBadgeSize           a W, L or T square on a schedule or last five row
   *   tableHeaderHeight         a standings or stats table header row
   *   tableCellWidth            one line score period column
   *   lineScoreRowHeight        one line score row, equal to `controlHeightSm` (not a target)
   *   tableTeamColumnMaxWidth   the line score's frozen team column
   *   statColumnWidth           one standings stat column
   *   standingsRankWidth        the standings rank column
   *   standingsTeamMinWidth     the standings team column on a phone
   *   playClockWidth            the clock column of a play row
   *   scheduleRowHeight         one team schedule row
   *   scheduleDateTileSize      the date tile at the start of a schedule row, one touch target
   */
  gameHeaderCenterMinWidth: 112,
  teamHeaderMarkSize: 56,
  playerHeaderPhotoSize: 72,
  rosterHeadshotSize: 36,
  tabIndicatorHeight: 2,
  accentEdgeWidth: 4,
  splitBarHeight: 4,
  livePillHeight: 24,
  resultBadgeSize: 24,
  tableHeaderHeight: 32,
  tableCellWidth: 32,
  lineScoreRowHeight: 36,
  tableTeamColumnMaxWidth: 96,
  statColumnWidth: 48,
  standingsRankWidth: 28,
  standingsTeamMinWidth: 140,
  playClockWidth: 52,
  scheduleRowHeight: 64,
  scheduleDateTileSize: 44,
} as const;

/* ------------------------------------------------------------------------- *
 * Type
 * ------------------------------------------------------------------------- */

export type typeTextStyle = {
  /** Device-independent pixels. */
  size: number;
  weight: number;
  /** Unitless multiplier — valid in CSS `line-height` and computable for RN. */
  lineHeight: number;
  /** Tabular figures, so a column of money or a live score never jitters. */
  tabular?: true;
};

/**
 * One family across the whole platform. Inter is self-hosted from
 * `@fontsource/inter`, latin subset, weights 400/600/700/800, imported by
 * `apps/app/src/index.css` and `apps/home/src/styles/global.css`. The stack
 * below is the fallback chain until it loads.
 *
 * Two surfaces deliberately do NOT resolve through this token and are not
 * defects: `apps/admin` ships stock Ant Design on the system stack by owner
 * instruction (see apps/admin/src/theme/AdminThemeProvider.tsx), and
 * `apps/mobile` names Inter in native.ts but does not yet load it
 * (ISSUE-549). Both are recorded, neither is accidental.
 */
const fontFamily = {
  sans: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
} as const;

/**
 * Body floor is 16px anywhere a control can trigger iOS Safari's
 * auto-zoom-on-focus. `input` and `inputLg` are pinned there for that reason —
 * do not "tighten" them.
 */
const text = {
  display: { size: 32, weight: 800, lineHeight: 1.15 },
  h1: { size: 25, weight: 800, lineHeight: 1.2 },
  h2: { size: 18, weight: 700, lineHeight: 1.3 },
  body: { size: 16, weight: 400, lineHeight: 1.5 },
  bodyStrong: { size: 16, weight: 700, lineHeight: 1.5 },
  /** Section labels — sentence case, never an uppercase overline. */
  caption: { size: 13.5, weight: 600, lineHeight: 1.4 },
  /** Legal text and fine print. The smallest size used anywhere. */
  small: { size: 12, weight: 400, lineHeight: 1.4 },
  input: { size: 16, weight: 400, lineHeight: 1.5 },
  inputLg: { size: 16, weight: 400, lineHeight: 1.5 },
  moneyHero: { size: 32, weight: 800, lineHeight: 1.15, tabular: true },
  moneyKpi: { size: 27, weight: 800, lineHeight: 1.2, tabular: true },
  moneyTotal: { size: 18, weight: 800, lineHeight: 1.3, tabular: true },
  moneyRow: { size: 16, weight: 800, lineHeight: 1.4, tabular: true },
  score: { size: 20, weight: 800, lineHeight: 1.2, tabular: true },
  /** Retired from score rows (Scores UX spec v1.0 section 1.3: one score size
   *  for every row state, so widths never jump). Legal in the game header only. */
  scoreLive: { size: 24, weight: 800, lineHeight: 1.2, tabular: true },
} as const satisfies Record<string, typeTextStyle>;

/* ------------------------------------------------------------------------- *
 * Elevation
 * ------------------------------------------------------------------------- */

/**
 * Shadows are stored as geometry plus opacity, not as a CSS string, because
 * React Native cannot parse a `box-shadow`. `tokensCss.ts` composes the web
 * string; `native.ts` composes the RN shadow object. One definition either way.
 */
export type typeShadow = {
  offsetY: number;
  blur: number;
  spread: number;
  /** Opacity of the shadow color, 0-1. Dark mode multiplies this (see `shadowBoost`). */
  opacity: number;
};

const elevation = {
  /** Flat on the page. */
  none: { offsetY: 0, blur: 0, spread: 0, opacity: 0 },
  /** A resting card. */
  sm: { offsetY: 1, blur: 3, spread: 0, opacity: 0.1 },
  /** A popover, dropdown, or tooltip. */
  md: { offsetY: 4, blur: 12, spread: -2, opacity: 0.14 },
  /** A dialog, bottom sheet, or drawer. */
  lg: { offsetY: 16, blur: 32, spread: -8, opacity: 0.22 },
} as const satisfies Record<string, typeShadow>;

/**
 * Dark mode needs a heavier shadow to read at all against a near-black page.
 * Applied as a multiplier on `opacity` so the geometry stays identical.
 */
const shadowBoost = { light: 1, dark: 1.8 } as const;

/** The shadow color, per mode. Dark uses pure black; light uses the ink neutral. */
const shadowColor = { light: neutral[975], dark: neutral[1000] } as const;

/* ------------------------------------------------------------------------- *
 * Motion
 * ------------------------------------------------------------------------- */

/**
 * Milliseconds. Nothing pops. Ease-out on entrances (a slight
 * overshoot that reads as responsive), ease-in on exits (accelerate out,
 * never linger).
 */
const motion = {
  duration: {
    /** Micro state changes — a checkbox flip, a press-down. */
    fast: 100,
    /** Most transitions — a tab change, a sheet content swap. */
    mid: 200,
    /** Sheet, dialog, and drawer enter/exit. */
    slow: 300,
  },
  easing: {
    enter: "cubic-bezier(0.2, 0.8, 0.2, 1)",
    exit: "cubic-bezier(0.4, 0, 1, 1)",
    /** Symmetric, for a property that moves both ways in one gesture. */
    standard: "cubic-bezier(0.4, 0, 0.2, 1)",
  },
  /** Reanimated / Framer Motion spring preset for sheet and card transitions. */
  spring: {
    smooth: { duration: 0.35, bounce: 0 },
  },
} as const;

/* ------------------------------------------------------------------------- *
 * Stacking
 * ------------------------------------------------------------------------- */

/**
 * The shared error modal outranks every other overlay,
 * including one raised from inside an open sheet. No feature may declare its
 * own z-index — take one of these or the ordering stops being knowable.
 * A menu raised from inside a sheet takes `dropdown`, which outranks `dialog`
 * and `drawer` so it is never painted behind the surface that opened it
 * (ISSUE-904). It stays under `toast`, `tooltip` and `errorModal`. It
 * therefore also outranks `scrim`, which is correct and unobservable: a scrim
 * exists only while a dialog or a drawer is open, and those are modal, so the
 * only select that can be open beside one is a select inside it.
 */
const zIndex = {
  base: 0,
  sticky: 20,
  dropdown: 55,
  scrim: 40,
  dialog: 50,
  drawer: 50,
  toast: 60,
  tooltip: 70,
  errorModal: 90,
} as const;

/* ------------------------------------------------------------------------- *
 * The exported token object
 * ------------------------------------------------------------------------- */

export const tokens = {
  palette,
  color: { light, dark },
  marketingColors,
  marketingOverlay,
  space,
  radius,
  size,
  fontFamily,
  text,
  elevation,
  shadowBoost,
  shadowColor,
  motion,
  zIndex,
} as const;

export type typeTokens = typeof tokens;
export type typeColorMode = keyof typeof tokens.color;
export type typeSpaceToken = keyof typeof space;
export type typeRadiusToken = keyof typeof radius;
export type typeTextToken = keyof typeof text;
export type typeElevationToken = keyof typeof elevation;

/* ------------------------------------------------------------------------- *
 * The contrast contract
 * ------------------------------------------------------------------------- */

/**
 * Every foreground/background pair the platform actually renders, with the
 * WCAG 2.2 AA floor it must clear. `tokens.test.ts` walks this list in BOTH
 * modes and asserts the computed ratio — the platform's accessibility claim
 * is only true because this runs.
 *
 * Deliberately not listed:
 *  - `borderHairline` — a decorative divider, exempt under §1.4.11.
 *  - `disabled*` — disabled controls are exempt under §1.4.3 and §1.4.11. We
 *    still assert 3:1 on `disabledText` below, because "exempt" and "invisible"
 *    are not the same thing to a fan trying to read why a button is off.
 *  - `scrim` / `skeleton*` — non-informational.
 */
export type typeContrastContractKey =
  keyof typeSemanticColors | typeMarketingColorToken;

export const CONTRAST_CONTRACT: readonly {
  foreground: typeContrastContractKey;
  background: typeContrastContractKey;
  floor: "normalText" | "largeText" | "nonText";
  why: string;
}[] = [
  // Body copy on every surface it can land on.
  {
    foreground: "text",
    background: "bg",
    floor: "normalText",
    why: "body copy on the page",
  },
  {
    foreground: "text",
    background: "surface",
    floor: "normalText",
    why: "body copy in a card",
  },
  {
    foreground: "text",
    background: "surfaceElevated",
    floor: "normalText",
    why: "body copy in a dialog",
  },
  {
    foreground: "text",
    background: "surfaceSunken",
    floor: "normalText",
    why: "body copy in a well",
  },
  {
    foreground: "textSecondary",
    background: "bg",
    floor: "normalText",
    why: "supporting facts on the page",
  },
  {
    foreground: "textSecondary",
    background: "surface",
    floor: "normalText",
    why: "supporting facts in a card",
  },
  {
    foreground: "textSecondary",
    background: "surfaceElevated",
    floor: "normalText",
    why: "supporting facts in a dialog",
  },
  {
    foreground: "textTertiary",
    background: "bg",
    floor: "normalText",
    why: "input placeholder on the page",
  },
  {
    foreground: "textTertiary",
    background: "surface",
    floor: "normalText",
    why: "input placeholder in a card",
  },
  {
    foreground: "textTertiary",
    background: "surfaceElevated",
    floor: "normalText",
    why: "input placeholder in a dialog",
  },

  // Brand.
  {
    foreground: "primary",
    background: "bg",
    floor: "normalText",
    why: "a link or a thin brand indicator on the page, the foreground blue and not the fill",
  },
  {
    foreground: "primary",
    background: "surface",
    floor: "normalText",
    why: "a link in a card",
  },
  {
    foreground: "primary",
    background: "surfaceElevated",
    floor: "normalText",
    why: "a link in a dialog",
  },
  {
    foreground: "primary",
    background: "primaryTint",
    floor: "normalText",
    why: "brand text on the tinted card",
  },
  {
    foreground: "text",
    background: "primaryTint",
    floor: "normalText",
    why: "body copy on the tinted card",
  },
  {
    foreground: "onPrimary",
    background: "primarySolid",
    floor: "normalText",
    why: "the primary button label",
  },
  {
    foreground: "onPrimary",
    background: "primarySolidHover",
    floor: "normalText",
    why: "the primary button label, hovered",
  },
  {
    foreground: "onPrimary",
    background: "primarySolidActive",
    floor: "normalText",
    why: "the primary button label, pressed",
  },
  {
    foreground: "primarySolid",
    background: "bg",
    floor: "nonText",
    why: "the filled primary button's own edge against the page",
  },
  {
    foreground: "primarySolid",
    background: "surface",
    floor: "nonText",
    why: "the filled primary button's own edge in a card",
  },

  // Accent (stadium blue, a secondary informational role; primary blue owns the brand).
  {
    foreground: "accent",
    background: "bg",
    floor: "normalText",
    why: "stadium-blue accent text on the page",
  },
  {
    foreground: "accent",
    background: "surface",
    floor: "normalText",
    why: "stadium-blue accent text in a card",
  },
  {
    foreground: "accent",
    background: "accentTint",
    floor: "normalText",
    why: "stadium-blue accent text on the stadium tint",
  },
  {
    foreground: "onAccent",
    background: "accentSolid",
    floor: "normalText",
    why: "a filled stadium-blue accent badge label",
  },

  // Status hues.
  {
    foreground: "success",
    background: "bg",
    floor: "normalText",
    why: '"Alive" on the page',
  },
  {
    foreground: "success",
    background: "surface",
    floor: "normalText",
    why: '"Alive" in a card',
  },
  {
    foreground: "success",
    background: "successTint",
    floor: "normalText",
    why: "success text on the success tint",
  },
  {
    foreground: "onSuccess",
    background: "successSolid",
    floor: "normalText",
    why: 'a filled "Won" badge label',
  },
  {
    foreground: "warning",
    background: "bg",
    floor: "normalText",
    why: '"Locking soon" on the page',
  },
  {
    foreground: "warning",
    background: "surface",
    floor: "normalText",
    why: '"Locking soon" in a card',
  },
  {
    foreground: "warning",
    background: "warningTint",
    floor: "normalText",
    why: "warning text on the warning tint",
  },
  {
    foreground: "onWarning",
    background: "warningSolid",
    floor: "normalText",
    why: "a filled warning badge label",
  },
  {
    foreground: "error",
    background: "bg",
    floor: "normalText",
    why: '"Eliminated" on the page',
  },
  {
    foreground: "error",
    background: "surface",
    floor: "normalText",
    why: '"Eliminated" in a card',
  },
  {
    foreground: "error",
    background: "errorTint",
    floor: "normalText",
    why: "error text on the error tint",
  },
  {
    foreground: "onError",
    background: "errorSolid",
    floor: "normalText",
    why: "the destructive button label",
  },
  {
    foreground: "onError",
    background: "errorHover",
    floor: "normalText",
    why: "the destructive button label, hovered",
  },
  {
    foreground: "info",
    background: "bg",
    floor: "normalText",
    why: "an informational banner on the page",
  },
  {
    foreground: "info",
    background: "surface",
    floor: "normalText",
    why: "an informational banner in a card",
  },
  {
    foreground: "info",
    background: "infoTint",
    floor: "normalText",
    why: "info text on the info tint",
  },
  {
    foreground: "onInfo",
    background: "infoSolid",
    floor: "normalText",
    why: "a filled info badge label",
  },

  // Inverted surfaces (tooltip in light mode, tooltip in dark mode).
  {
    foreground: "textInverse",
    background: "text",
    floor: "normalText",
    why: "a tooltip's label on its inverted surface",
  },

  // §1.4.11 non-text: control boundaries and the focus ring.
  {
    foreground: "border",
    background: "bg",
    floor: "nonText",
    why: "an input's boundary on the page",
  },
  {
    foreground: "border",
    background: "surface",
    floor: "nonText",
    why: "an input's boundary in a card",
  },
  {
    foreground: "border",
    background: "surfaceElevated",
    floor: "nonText",
    why: "an input's boundary in a dialog",
  },
  {
    foreground: "borderStrong",
    background: "surface",
    floor: "nonText",
    why: "a selected row's boundary",
  },
  {
    foreground: "focusRing",
    background: "bg",
    floor: "nonText",
    why: "the focus ring against the page",
  },
  {
    foreground: "focusRing",
    background: "surface",
    floor: "nonText",
    why: "the focus ring against a card",
  },
  {
    foreground: "focusRing",
    background: "surfaceElevated",
    floor: "nonText",
    why: "the focus ring against a dialog",
  },
  // §1.4.11 non-text: the two sides of a stat comparison bar (ISSUE-1084).
  {
    foreground: "comparisonAway",
    background: "bg",
    floor: "nonText",
    why: "the away segment of a stat comparison bar on the page",
  },
  {
    foreground: "comparisonAway",
    background: "surface",
    floor: "nonText",
    why: "the away segment of a stat comparison bar in a card",
  },
  {
    foreground: "comparisonAway",
    background: "surfaceElevated",
    floor: "nonText",
    why: "the away segment of a stat comparison bar in a dialog",
  },
  {
    foreground: "comparisonHome",
    background: "bg",
    floor: "nonText",
    why: "the home segment of a stat comparison bar on the page",
  },
  {
    foreground: "comparisonHome",
    background: "surface",
    floor: "nonText",
    why: "the home segment of a stat comparison bar in a card",
  },
  {
    foreground: "comparisonHome",
    background: "surfaceElevated",
    floor: "nonText",
    why: "the home segment of a stat comparison bar in a dialog",
  },
  // Not required by WCAG, held anyway: a disabled label a fan can still read.
  {
    foreground: "disabledText",
    background: "disabledBg",
    floor: "nonText",
    why: "a disabled control's label",
  },

  // Marketing surface (ISSUE-060). Scheme-invariant, so each of these resolves
  // to the same ratio in light and dark — `tokens.test.ts` grades both anyway,
  // which is what proves the invariance rather than asserting it.
  {
    foreground: "onInk",
    background: "ink",
    floor: "normalText",
    why: "every heading and paragraph on the homepage's dark sections",
  },
  {
    foreground: "brandOnInk",
    background: "ink",
    floor: "normalText",
    why: "the footer's Privacy / Opt Out / Terms / Cookie links — the live site puts #8E43F0 here at 3.78:1 and fails",
  },
  {
    foreground: "paper",
    background: "brand",
    floor: "normalText",
    why: "the Download IOS, Download Android, and SEND MESSAGE button labels",
  },
  {
    foreground: "paper",
    background: "facebook",
    floor: "nonText",
    why: "the Facebook tile glyph",
  },
  {
    foreground: "paper",
    background: "x",
    floor: "nonText",
    why: "the X tile glyph",
  },
  {
    foreground: "paper",
    background: "instagram",
    floor: "nonText",
    why: "the Instagram tile glyph",
  },
  {
    foreground: "ink",
    background: "onInk",
    floor: "normalText",
    why: "the footer's copyright line on its light bar — the live site renders white here at 1.12:1",
  },
  {
    foreground: "ink",
    background: "paper",
    floor: "normalText",
    why: "what a visitor types into a contact input",
  },
  {
    foreground: "brandOnLight",
    background: "paper",
    floor: "nonText",
    why: "the contact input's resting border and its focus ring",
  },
  {
    foreground: "brandOnLight",
    background: "onInk",
    floor: "normalText",
    why: "brand-colored text on the footer's light bar; graded even with no such link today, because this is what stops #8E43F0 landing there at 4.45:1",
  },
  {
    foreground: "fieldError",
    background: "ink",
    floor: "normalText",
    why: "a field-error message and the required marker",
  },
  {
    foreground: "fieldError",
    background: "paper",
    floor: "nonText",
    why: "an invalid contact input's border",
  },
  {
    foreground: "brand",
    background: "ink",
    floor: "nonText",
    why: "the solid brand button's own edge against the ink section — a SURFACE boundary, never text",
  },
];
